import type { ReactNode } from "react";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import type PartySocket from "partysocket";

import type { HandleGetHelpFunctionArgs } from "../Help/AiHelpCard";
import { useCallDisplay } from "./hooks/useCallDisplay";

export type CallContextType = ReturnType<typeof useCallDisplay> & {
  leaveCall: () => void;
  roomId: string;
  mainSocket: PartySocket;
  activeMode: CallDisplayMode;
  setActiveMode: React.Dispatch<React.SetStateAction<CallDisplayMode>>;
  handleGetAIHelp: (args: HandleGetHelpFunctionArgs) => void;
};

export type CallDisplayMode =
  | "ai"
  | "transcript"
  | "script"
  | "participants"
  | "debug"
  | "report"
  | "summary";

const CallContext = createContext<CallContextType | undefined>(undefined);

export interface CallProviderProps {
  children: ReactNode;
  roomId: string;
  leaveCall: () => void;
  mainSocket: PartySocket;
}

export function CallProvider({
  children,
  roomId,
  leaveCall,
  mainSocket,
}: Readonly<CallProviderProps>) {
  const callDisplayProps = useCallDisplay(roomId);
  const { handleGetHelp } = callDisplayProps;
  const [activeMode, setActiveMode] = useState<CallDisplayMode>("script");

  const handleGetAIHelp = useCallback(
    (args: HandleGetHelpFunctionArgs) => {
      console.log("Getting AI help");
      setActiveMode("ai");
      handleGetHelp(args);
    },
    [handleGetHelp],
  );

  const value = useMemo(
    () => ({
      ...callDisplayProps,
      leaveCall,
      roomId,
      mainSocket,
      activeMode,
      setActiveMode,
      handleGetAIHelp,
    }),
    [
      callDisplayProps,
      leaveCall,
      roomId,
      mainSocket,
      activeMode,
      handleGetAIHelp,
    ],
  );

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCallContext() {
  const context = useContext(CallContext);
  if (context === undefined) {
    throw new Error("useCallContext must be used within a CallProvider");
  }
  return context;
}
